import React from 'react';
import { Button, Typography, Divider } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { Customer, Vehicle } from '../../types';
import type { CustomerMatch } from '../../utils/customerMatcher';
import type { VehicleMatch } from '../../utils/vehicleMatcher';

const { Title, Text } = Typography;

export interface EstimateHeaderProps {
  isMobile: boolean;
  isEditing: boolean;
  selectedCustomer: Customer | null;
  selectedVehicle: Vehicle | null;
  onNavigateBack: () => void;
  onCustomerChange: (customer: Customer) => void;
  onVehicleChange: (vehicle: Vehicle) => void;
  onClearCustomer: () => void;
  onClearVehicle: () => void;
  onEditCustomer: () => void;
  onEditVehicle: () => void;
  customerCandidates: CustomerMatch[];
  vehicleCandidates: VehicleMatch[];
  onAcceptCustomerCandidate: (customer: Customer) => void;
  onDismissCustomerCandidates: () => void;
  onAcceptVehicleCandidate: (vehicle: Vehicle) => void;
  onDismissVehicleCandidates: () => void;
}

/** Header section: back navigation, title, customer/vehicle selection (mobile inline). */
export const EstimateHeader: React.FC<EstimateHeaderProps> = ({
  isMobile,
  isEditing,
  selectedCustomer,
  selectedVehicle,
  onNavigateBack,
  onCustomerChange,
  onVehicleChange,
  onClearCustomer,
  onClearVehicle,
  onEditCustomer,
  onEditVehicle,
  customerCandidates,
  vehicleCandidates,
  onAcceptCustomerCandidate,
  onDismissCustomerCandidates,
  onAcceptVehicleCandidate,
  onDismissVehicleCandidates,
}) => {
  if (isMobile) {
    // Customer & Vehicle is owned by CustomerVehiclePanel (the top card on
    // narrow widths), so the mobile header is just the title + back action.
    return (
      <div style={{
        padding: '10px 16px',
        borderBottom: '1px solid #e5e7eb',
        backgroundColor: '#ffffff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexShrink: 0,
      }}>
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={onNavigateBack}
          style={{ padding: '4px 0', height: 'auto', fontWeight: 500 }}
        >
          Back
        </Button>
        <Title level={5} style={{ margin: 0 }}>
          {isEditing ? 'Edit Estimate' : 'New Estimate'}
        </Title>
        <div style={{ width: 60 }} />
      </div>
    );
  }

  // Desktop header
  return (
    <div style={{
      padding: '16px 24px',
      borderBottom: '1px solid #e5e7eb',
      backgroundColor: '#ffffff',
      zIndex: 10,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
        <Button type="text" icon={<ArrowLeftOutlined />} onClick={onNavigateBack}>
          Back to Estimates
        </Button>
        <Divider type="vertical" />
        <Title level={3} style={{ margin: 0 }}>
          {isEditing ? 'Edit Estimate' : 'New Estimate'}
        </Title>
        {selectedCustomer && selectedVehicle && (
          <>
            <Divider type="vertical" />
            <Text type="secondary">
              {selectedCustomer.company_name || `${selectedCustomer.first_name} ${selectedCustomer.last_name}`} &bull;{' '}
              {selectedVehicle.year} {selectedVehicle.make} {selectedVehicle.model}
            </Text>
          </>
        )}
      </div>
    </div>
  );
};
